import { useState, useEffect } from 'react'

export interface User {
  firstName: string
  lastName: string
  email: string
}

const COOKIE_NAME = 'drainage_code_user'
const COOKIE_DAYS = 365

function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null
  return null
}

function setCookie(name: string, value: string, days: number) {
  let expires = ''
  if (days) {
    const date = new Date()
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
    expires = '; expires=' + date.toUTCString()
  }
  document.cookie = name + '=' + (value || '') + expires + '; path=/'
}

function deleteCookie(name: string) {
  document.cookie = name + '=; Max-Age=-99999999; path=/'
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('signup')

  useEffect(() => {
    // Check for cookie on mount
    const cookieData = getCookie(COOKIE_NAME)
    if (cookieData) {
      try {
        const parsedUser = JSON.parse(decodeURIComponent(cookieData))
        if (parsedUser && parsedUser.firstName) {
          setUser(parsedUser)
          setIsAuthenticated(true)
        }
      } catch (e) {
        console.error('Failed to parse auth cookie', e)
      }
    }
  }, [])

  const login = (email: string) => {
    // Look up the user from localStorage by email
    try {
      const stored = localStorage.getItem('drainage_code_users')
      if (stored) {
        const users: User[] = JSON.parse(stored)
        const found = users.find(
          (u) => u.email.toLowerCase() === email.toLowerCase(),
        )
        if (found) {
          setUser(found)
          setIsAuthenticated(true)
          setCookie(
            COOKIE_NAME,
            encodeURIComponent(JSON.stringify(found)),
            COOKIE_DAYS,
          )
          return
        }
      }
    } catch (e) {
      console.error('Failed to look up user', e)
    }
    // If no stored user found, use the email prefix as a fallback name
    const fallbackName = email.split('@')[0]
    const fallbackUser: User = {
      firstName: fallbackName.charAt(0).toUpperCase() + fallbackName.slice(1),
      lastName: '',
      email,
    }
    setUser(fallbackUser)
    setIsAuthenticated(true)
    setCookie(
      COOKIE_NAME,
      encodeURIComponent(JSON.stringify(fallbackUser)),
      COOKIE_DAYS,
    )
  }

  const signup = (firstName: string, lastName: string, email: string) => {
    const newUser: User = { firstName, lastName, email }
    // Save to localStorage so login can find them later
    try {
      const stored = localStorage.getItem('drainage_code_users')
      const users: User[] = stored ? JSON.parse(stored) : []
      // Replace if same email exists, otherwise add
      const filtered = users.filter(
        (u) => u.email.toLowerCase() !== email.toLowerCase(),
      )
      filtered.push(newUser)
      localStorage.setItem('drainage_code_users', JSON.stringify(filtered))
    } catch (e) {
      console.error('Failed to save user', e)
    }
    setUser(newUser)
    setIsAuthenticated(true)
    setCookie(
      COOKIE_NAME,
      encodeURIComponent(JSON.stringify(newUser)),
      COOKIE_DAYS,
    )
  }

  const logout = () => {
    setUser(null)
    setIsAuthenticated(false)
    deleteCookie(COOKIE_NAME)
  }

  return {
    user,
    isAuthenticated,
    authMode,
    setAuthMode,
    login,
    signup,
    logout,
  }
}
